/**
 * Processing Pipeline — main thread orchestrator.
 *
 * This module is the only place in the app that:
 * 1. Detects file category from magic bytes
 * 2. Writes originals to OPFS immediately (before processing starts)
 * 3. Registers files in IndexedDB with status = 'pending'
 * 4. Dispatches jobs to the appropriate worker pool
 * 5. Updates IndexedDB status as jobs progress
 * 6. Calls onProgress so the UI can react without polling
 *
 * The pipeline is intentionally NOT a class — it's a set of pure async functions
 * that share state via the singleton storage modules.
 */

import * as Comlink from 'comlink'
import { nanoid } from './nanoid'
import { mapConcurrent } from './concurrency'
import { imagePool } from '../workers/worker-pool'
import { writeToOPFS, readFromOPFS } from './storage/opfs'
import { upsertFileRecord } from './storage/idb'
import type { FileRecord, MediaCategory, ProcessedResult } from './storage/types'

export const INGEST_CONCURRENCY = 2
export const PROCESS_CONCURRENCY = Math.min(
  typeof navigator !== 'undefined' ? navigator.hardwareConcurrency || 2 : 2,
  4,
)

export interface ProcessingOptions {
  quality: {
    avif: number
    webp: number
    jpeg: number
  }
  generateFavicons: boolean
  video?: {
    codec: 'h264' | 'av1' | 'webm'
    mode: 'auto' | 'local' | 'cloud'
    maxDimension: number
  }
}

export const DEFAULT_OPTIONS: ProcessingOptions = {
  quality: { avif: 50, webp: 80, jpeg: 82 },
  generateFavicons: true,
  video: {
    codec: 'h264',
    mode: 'auto',
    maxDimension: 1920,
  },
}

export type ProgressCallback = (record: FileRecord) => void

// ─── Magic Byte Detection ─────────────────────────────────────────────────────

const _MAGIC_SIGNATURES: [number[], string, MediaCategory][] = [
  [[0xff, 0xd8, 0xff], 'image/jpeg', 'image'],
  [[0x89, 0x50, 0x4e, 0x47], 'image/png', 'image'],
  [[0x47, 0x49, 0x46], 'image/gif', 'gif'],
  [[0x52, 0x49, 0x46, 0x46], 'image/webp', 'image'], // also checks WEBP at +8
  [[0x00, 0x00, 0x00], 'video/mp4', 'video'], // ftyp box — refined below
  [[0x1a, 0x45, 0xdf, 0xa3], 'video/webm', 'video'],
  [[0x4f, 0x67, 0x67, 0x53], 'audio/ogg', 'audio'],
  [[0x49, 0x44, 0x33], 'audio/mpeg', 'audio'],
  [[0x66, 0x4c, 0x61, 0x43], 'audio/flac', 'audio'],
  [[0x52, 0x49, 0x46, 0x46], 'audio/wav', 'audio'], // also checks WAVE at +8
]

function detectFromMagicBytes(buffer: ArrayBuffer): { mimeType: string; category: MediaCategory } {
  const bytes = new Uint8Array(buffer, 0, 16)

  // JPEG
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
    return { mimeType: 'image/jpeg', category: 'image' }
  }
  // PNG
  if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return { mimeType: 'image/png', category: 'image' }
  }
  // GIF
  if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
    return { mimeType: 'image/gif', category: 'gif' }
  }
  // RIFF container: WEBP or WAV
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
    const subType = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11])
    if (subType === 'WEBP') return { mimeType: 'image/webp', category: 'image' }
    if (subType === 'WAVE') return { mimeType: 'audio/wav', category: 'audio' }
  }
  // ftyp box: MP4, MOV, AVIF
  const brand = String.fromCharCode(bytes[4], bytes[5], bytes[6], bytes[7])
  if (brand === 'ftyp') {
    const sub = String.fromCharCode(bytes[8], bytes[9], bytes[10], bytes[11])
    if (sub === 'avif' || sub === 'avis') return { mimeType: 'image/avif', category: 'image' }
    if (sub === 'qt  ' || sub === 'M4V ' || sub === 'M4A ') return { mimeType: 'video/quicktime', category: 'video' }
    return { mimeType: 'video/mp4', category: 'video' }
  }
  // WebM / MKV
  if (bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
    return { mimeType: 'video/webm', category: 'video' }
  }
  // OGG
  if (bytes[0] === 0x4f && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
    return { mimeType: 'audio/ogg', category: 'audio' }
  }
  // MP3 (ID3 tag)
  if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
    return { mimeType: 'audio/mpeg', category: 'audio' }
  }
  // FLAC
  if (bytes[0] === 0x66 && bytes[1] === 0x4c && bytes[2] === 0x61 && bytes[3] === 0x43) {
    return { mimeType: 'audio/flac', category: 'audio' }
  }
  // WAV
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
    return { mimeType: 'audio/wav', category: 'audio' }
  }

  return { mimeType: 'application/octet-stream', category: 'unknown' }
}

function isSVG(file: File): boolean {
  if (file.type === 'image/svg+xml') return true
  if (file.name.toLowerCase().endsWith('.svg')) return true
  return false
}

// ─── File Ingestion ───────────────────────────────────────────────────────────

/**
 * Ingests a File into the pipeline:
 * 1. Reads first 16 bytes for magic detection
 * 2. Writes full binary to OPFS
 * 3. Registers record in IndexedDB with status='pending'
 * Returns the FileRecord (caller can begin queuing it for processing)
 */
export async function ingestFile(file: File): Promise<FileRecord> {
  const id = nanoid()
  const headerBuffer = await file.slice(0, 16).arrayBuffer()

  let mimeType: string
  let category: MediaCategory

  if (isSVG(file)) {
    mimeType = 'image/svg+xml'
    category = 'svg'
  } else {
    const detected = detectFromMagicBytes(headerBuffer)
    mimeType = detected.mimeType
    category = detected.category
  }

  const opfsPath = `originals/${id}`
  const fullBuffer = await file.arrayBuffer()
  await writeToOPFS(opfsPath, fullBuffer)

  const record: FileRecord = {
    id,
    name: file.name,
    originalName: file.name,
    mimeType,
    category,
    originalSize: file.size,
    status: 'pending',
    createdAt: Date.now(),
    updatedAt: Date.now(),
    opfsPath,
    results: [],
  }

  await upsertFileRecord(record)
  return record
}

// ─── Processing Dispatch ──────────────────────────────────────────────────────

/**
 * Processes a single FileRecord that is already in OPFS.
 * Updates IndexedDB on start, completion, and error.
 * Calls onProgress after every state change.
 */
export async function processFile(
  record: FileRecord,
  options: ProcessingOptions,
  onProgress: ProgressCallback,
): Promise<FileRecord> {
  const processingRecord: FileRecord = { ...record, status: 'processing', updatedAt: Date.now() }
  await upsertFileRecord(processingRecord)
  onProgress(processingRecord)

  try {
    let updated: FileRecord

    if (record.category === 'image') {
      updated = await processImageFile(processingRecord, options, onProgress)
    } else if (record.category === 'svg') {
      updated = await processSVGFile(processingRecord, options, onProgress)
    } else if (record.category === 'video') {
      updated = await processVideoFile(processingRecord, options, onProgress)
    } else {
      // Unsupported category — mark as error with helpful message
      updated = {
        ...processingRecord,
        status: 'error',
        error: `El formato de ${record.category} aún no está soportado`,
        updatedAt: Date.now(),
      }
    }

    await upsertFileRecord(updated)
    onProgress(updated)
    return updated
  } catch (err) {
    const errorRecord: FileRecord = {
      ...processingRecord,
      status: 'error',
      error: err instanceof Error ? err.message : 'Unknown processing error',
      updatedAt: Date.now(),
    }
    await upsertFileRecord(errorRecord)
    onProgress(errorRecord)
    return errorRecord
  }
}

async function processImageFile(
  record: FileRecord,
  options: ProcessingOptions,
  _onProgress: ProgressCallback,
): Promise<FileRecord> {
  const inputBuffer = await readFromOPFS(record.opfsPath)
  if (!inputBuffer) throw new Error(`Original file not found in OPFS: ${record.opfsPath}`)

  const result = await imagePool.submit({
    fileId: record.id,
    fileName: record.name,
    inputBuffer,
    mimeType: record.mimeType,
    quality: options.quality,
  })

  const baseName = record.name.replace(/\.[^/.]+$/, '')
  const results: ProcessedResult[] = result.outputs.map((o) => {
    const ext = o.format === 'jpeg' ? 'jpg' : o.format
    return {
      format: o.format,
      filename: `${baseName}.${ext}`,
      label: o.format.toUpperCase(),
      size: o.size,
      opfsPath: o.opfsPath,
      savingsPercent: Math.round((1 - o.size / record.originalSize) * 100),
    }
  })

  return {
    ...record,
    status: 'done',
    results,
    updatedAt: Date.now(),
  }
}

// SVG worker singleton — created once, reused for all SVG jobs
let svgWorkerProxy: Comlink.Remote<{ processSVG: typeof import('../workers/svg-processor.worker')['processSVG'] }> | null = null

function getSVGWorkerProxy() {
  if (!svgWorkerProxy) {
    const worker = new Worker(new URL('../workers/svg-processor.worker.ts', import.meta.url), { type: 'module' })
    svgWorkerProxy = Comlink.wrap(worker)
  }
  return svgWorkerProxy
}

async function processSVGFile(
  record: FileRecord,
  options: ProcessingOptions,
  _onProgress: ProgressCallback,
): Promise<FileRecord> {
  const inputBuffer = await readFromOPFS(record.opfsPath)
  if (!inputBuffer) throw new Error(`Original file not found in OPFS: ${record.opfsPath}`)

  const svgSource = new TextDecoder().decode(inputBuffer)
  const proxy = getSVGWorkerProxy()

  // 1. Optimize SVG markup in worker
  const result = await proxy.processSVG({
    fileId: record.id,
    svgSource,
  })

  const baseName = record.name.replace(/\.[^/.]+$/, '')
  const results: ProcessedResult[] = [
    {
      format: 'svg',
      filename: `${baseName}.svg`,
      label: 'SVG Optimizado',
      size: result.optimizedSize,
      opfsPath: result.opfsPath,
      savingsPercent: Math.round((1 - result.optimizedSize / record.originalSize) * 100),
    },
  ]

  // 2. Generate Favicon package on main thread where DOM & Canvas rendering are available
  if (options.generateFavicons) {
    const { generateFaviconPackage } = await import('./svg-rasterizer')
    const faviconResults = await generateFaviconPackage(
      record.id,
      result.optimizedSvg,
      record.originalSize,
    )
    results.push(...faviconResults)
  }

  return {
    ...record,
    status: 'done',
    results,
    updatedAt: Date.now(),
  }
}

async function processVideoFile(
  record: FileRecord,
  options: ProcessingOptions,
  onProgress: ProgressCallback,
): Promise<FileRecord> {
  const inputBuffer = await readFromOPFS(record.opfsPath)
  if (!inputBuffer) throw new Error(`Video original no encontrado en OPFS: ${record.opfsPath}`)

  const { routeAndProcessVideo } = await import('./video/video-router')
  const results = await routeAndProcessVideo(
    record.id,
    record.name,
    inputBuffer,
    record.originalSize,
    {
      codec: options.video?.codec || 'h264',
      mode: options.video?.mode || 'auto',
      maxDimension: options.video?.maxDimension || 1920,
      onProgress: (_percent) => {
        onProgress({
          ...record,
          status: 'processing',
          updatedAt: Date.now(),
        })
      },
    },
  )

  return {
    ...record,
    status: 'done',
    results,
    updatedAt: Date.now(),
  }
}

// ─── Batch Processing ─────────────────────────────────────────────────────────

/**
 * Ingests and processes a batch of Files with strictly bounded concurrency.
 *
 * Architecture:
 * 1. Ingestion: Reads files and writes them to OPFS in a sliding window of max 2 files
 *    simultaneously, reducing peak Heap memory from >1.2 GB to ~30-45 MB.
 * 2. Processing: Pulls records from OPFS into workers bounded by PROCESS_CONCURRENCY,
 *    preventing the pool queue from hoarding uncompressed buffers in main thread RAM.
 */
export async function processBatch(
  files: File[],
  options: ProcessingOptions,
  onProgress: ProgressCallback,
): Promise<FileRecord[]> {
  // 1. Ingest files with bounded concurrency and notify UI progressively
  const records = await mapConcurrent(files, INGEST_CONCURRENCY, async (file) => {
    const record = await ingestFile(file)
    onProgress(record)
    return record
  })

  // 2. Process records bounded by worker pool throughput
  const results = await mapConcurrent(records, PROCESS_CONCURRENCY, async (record) => {
    return processFile(record, options, onProgress)
  })

  return results
}
